/**
 * Mark-Arrived incomplete-patient-data guard (PROD-989).
 *
 * Flutter blocks the transition to `Arrived` when the patient's identity
 * data is incomplete, prompting the user to complete the patient first
 * (`calendar_tab.dart:6451-6468` `onStatusChange` + `:2259-2281` row action
 * → `showIncompletePatientDataAlert`). React previously fired the Arrived
 * status mutation unconditionally — this helper restores the guard.
 *
 * Flutter logic (verbatim intent):
 *   - `kind: 'patient'` — `isMissingData == true` OR (the patient has
 *     neither their own identification id NOR a guardian identification id).
 *     Message: "You need to enter patient identity details to make this
 *     appointment arrived".
 *   - `kind: 'guardian'` — patient is a MINOR (birthDate is more recent than
 *     the adult cutoff: now − (18y + 1d)) AND has no guardian relation set.
 *     Message: "You need to enter patient's guardian details to make this
 *     appointment arrived".
 *   - otherwise not blocked → the Arrived mutation proceeds.
 *
 * Precedence note: Flutter's source ORs `isMissingData` OUTSIDE the
 * `code == Arrived` guard (operator-precedence quirk), so it would also
 * alert on non-Arrived transitions. The alert copy is Arrived-specific, so
 * React gates the WHOLE guard on Arrived only — other transitions (Check-in,
 * No-Show, Cancel) are never blocked by incomplete data. This is an
 * INTENTIONAL product decision, not an oversight: signed off by Andrew on
 * 2026-06-03 (PR #442 review) as the deliberate spec, treating Flutter's
 * wider OR as a precedence bug. Do not "restore" the wider behaviour without
 * a new product sign-off.
 */

/** Minimal patient shape the guard needs — all fields optional/nullable. */
export interface ArrivedGuardPatient {
  /** Patient's own identification id (`patient.identificationId`). */
  identificationId?: string | null;
  /** Guardian's identification id (`guardianDetails.guaardianIdentificationId`). */
  guardianIdentificationId?: string | null;
  /** Guardian relation id (`guardianDetails.relationId`). */
  guardianRelationId?: string | null;
  /** ISO birth date (`patient.birthDate` / `dateOfBirth`). */
  birthDateISO?: string | null;
  /** Backend "data incomplete" flag (`patient.isMissingData`). */
  isMissingData?: boolean | null;
}

export type ArrivedGuardKind = 'patient' | 'guardian' | null;

export interface ArrivedGuardResult {
  blocked: boolean;
  kind: ArrivedGuardKind;
}

const NOT_BLOCKED: ArrivedGuardResult = { blocked: false, kind: null };

function isBlankStr(v: string | null | undefined): boolean {
  return !v || v.trim().length === 0;
}

/**
 * The adult cutoff Flutter uses: `now - (365*18 + 1) days`
 * (`utils.dart:1427`). A patient is a MINOR when their birth date is AFTER
 * this cutoff (i.e. born within the last 18 years).
 */
function isMinor(birthDateISO: string | null | undefined, now: Date): boolean {
  if (isBlankStr(birthDateISO)) return false;
  const dob = new Date(birthDateISO as string);
  if (Number.isNaN(dob.getTime())) return false;
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - (365 * 18 + 1));
  return dob.getTime() > cutoff.getTime();
}

/**
 * Returns whether the Arrived transition is blocked and, if so, which
 * incomplete-data alert to show. Pass `now` for deterministic tests;
 * defaults to the current time.
 */
export function arrivedGuardStatus(
  patient: ArrivedGuardPatient | null | undefined,
  now: Date = new Date(),
): ArrivedGuardResult {
  if (!patient) return NOT_BLOCKED;

  const idEmpty = isBlankStr(patient.identificationId);
  const guardianIdEmpty = isBlankStr(patient.guardianIdentificationId);

  // Patient-identity branch: explicit missing-data flag, or no identity at
  // all (neither own id nor guardian id).
  if (patient.isMissingData === true || (idEmpty && guardianIdEmpty)) {
    return { blocked: true, kind: 'patient' };
  }

  // Guardian branch: a minor with no guardian relation recorded.
  if (isMinor(patient.birthDateISO, now) && isBlankStr(patient.guardianRelationId)) {
    return { blocked: true, kind: 'guardian' };
  }

  return NOT_BLOCKED;
}

/** Verbatim Flutter alert copy (`calendar_tab.dart:6361-6365`). */
export const ARRIVED_GUARD_TITLE = 'Incomplete patient data';
export function arrivedGuardMessage(kind: ArrivedGuardKind): string {
  return kind === 'guardian'
    ? "You need to enter patient's guardian details to make this appointment arrived"
    : 'You need to enter patient identity details to make this appointment arrived';
}
export const ARRIVED_GUARD_PROCEED = 'Proceed to complete patient';
